import { useContext, useMemo } from "react";
import {
  ChagresBalanceTitle,
  ChagresBalanceValue,
  ChagresMainBlock,
  ChagresTitle,
  ChagresValue,
  ChargesMain,
} from "components/styles";
import { formatPrice } from "helpers/formatNumber";
import { getPromotionSummary } from "helpers/promotionCharges";
import { TOContext } from "context/TContext";

export const TABLE_ORDERING_TYPE = {
  delivery: "delivery",
  catering_delivery: "catering_delivery",
  phone_order_delivery: "phone_order_delivery",
};

export const TABLE_ORDERING_TYPE_KEYS = [
  "delivery",
  "catering_delivery",
  "phone_order_delivery",
];

const Charges = () => {
  const { charges, orderType, isRefundDueShow, isDriver } =
    useContext(TOContext);

  const calculateServicingFee = useMemo(() => {
    const {
      ordersCoFee = 0,
      processingFee = 0,
      marketplaceFee = 0,
    } = charges?.servicingFee || {};
    return (
      ordersCoFee + processingFee + marketplaceFee + charges?.taxAmount || 0
    );
  }, [charges?.servicingFee, charges?.taxAmount]);

  const couponData = useMemo(() => {
    const match = charges?.discountDetails?.find((detail) => detail?.couponId);
    return match ?? null;
  }, [charges]);

  const discountValue = useMemo(() => {
    const match = charges?.discountDetails?.reduce((acc, detail) => {
      if (detail?.couponId) {
        return acc; // Skip if there's a couponId (do nothing)
      }
      return acc + (detail?.discountAmount || 0); // Reduce discountAmount if no couponId
    }, 0);

    return match; // Return the final reduced value
  }, [charges]);

  const promotionSummary = useMemo(
    () => getPromotionSummary(charges),
    [charges],
  );

  const standaloneCouponAmount = useMemo(() => {
    if (promotionSummary.hasPromotionDetails) {
      return 0;
    }
    return (
      Math.round(
        Math.abs(Number(couponData?.discountAmount || charges?.discount || 0)) *
          100,
      ) / 100
    );
  }, [
    charges?.discount,
    couponData?.discountAmount,
    promotionSummary.hasPromotionDetails,
  ]);

  return (
    <>
      <hr className="line-header" />
      <ChargesMain>
        <ChagresMainBlock>
          <ChagresTitle>Subtotal:</ChagresTitle>
          <ChagresValue sx={{ fontSize: "16px" }}>
            {formatPrice(charges?.subTotal)}
          </ChagresValue>
        </ChagresMainBlock>
        {discountValue ? (
          <ChagresMainBlock>
            <ChagresTitle>Discount:</ChagresTitle>
            <ChagresValue>({formatPrice(discountValue)})</ChagresValue>
          </ChagresMainBlock>
        ) : null}
        {/* {standaloneCouponAmount > 0 ? (
          <ChagresMainBlock>
            <ChagresTitle>Coupon:</ChagresTitle>
            <ChagresValue>
              {formatPrice(standaloneCouponAmount, "en-US", "USD", true)}
            </ChagresValue>
          </ChagresMainBlock>
        ) : null} */}
        {promotionSummary.coupons.map((coupon) => (
          <ChagresMainBlock key={coupon.key}>
            <ChagresTitle>
              {coupon.label ? `Coupon (${coupon.label}):` : "Coupon:"}
            </ChagresTitle>
            <ChagresValue>
              {formatPrice(coupon.amount, "en-US", "USD", true)}
            </ChagresValue>
          </ChagresMainBlock>
        ))}
        {promotionSummary.promotions.length
          ? promotionSummary.promotions.map((promotion) => (
              <ChagresMainBlock key={promotion.key}>
                <ChagresTitle>
                  {promotion.label
                    ? `Promotions (${promotion.label}):`
                    : "Promotions:"}
                </ChagresTitle>
                <ChagresValue>
                  {formatPrice(promotion.amount, "en-US", "USD", true)}
                </ChagresValue>
              </ChagresMainBlock>
            ))
          : promotionSummary.groupedAmount > 0 && (
              <ChagresMainBlock>
                <ChagresTitle>Promotions:</ChagresTitle>
                <ChagresValue>
                  {formatPrice(
                    promotionSummary.groupedAmount,
                    "en-US",
                    "USD",
                    true,
                  )}
                </ChagresValue>
              </ChagresMainBlock>
            )}
        {TABLE_ORDERING_TYPE_KEYS.includes(orderType) ? (
          <ChagresMainBlock>
            <ChagresTitle>Delivery Fee:</ChagresTitle>
            <ChagresValue>{formatPrice(charges?.deliveryFee)}</ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        {charges?.tipInfo?.kitchen > 0 || charges?.tipInfo?.driver > 0 ? (
          <ChagresMainBlock>
            <ChagresTitle>Tips:</ChagresTitle>
            <ChagresValue>
              {formatPrice(
                (charges?.tipInfo?.kitchen || 0) +
                  (charges?.tipInfo?.driver || 0),
              )}
            </ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        {charges?.tipInfo?.kitchen && charges?.tipInfo?.kitchen > 0 ? (
          <ChagresMainBlock sx={{ ml: 2, mr: 4 }}>
            <ChagresTitle>
              <i>Staff Tip:</i>
            </ChagresTitle>
            <ChagresValue>
              <i>{formatPrice(charges?.tipInfo?.kitchen)}</i>
            </ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        {(charges?.tipInfo?.driver &&
          charges?.tipInfo?.driver > 0 &&
          TABLE_ORDERING_TYPE_KEYS.includes(orderType)) ||
        (isDriver && charges?.tipInfo?.driver > 0) ? (
          <ChagresMainBlock sx={{ ml: 2, mr: 4 }}>
            <ChagresTitle>
              <i>Driver Tip:</i>
            </ChagresTitle>
            <ChagresValue>
              <i>{formatPrice(charges?.tipInfo?.driver)}</i>
            </ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        <ChagresMainBlock>
          <ChagresTitle>Taxes and Fees:</ChagresTitle>
          <ChagresValue>{formatPrice(calculateServicingFee)}</ChagresValue>
        </ChagresMainBlock>
        {charges?.taxAmount && charges?.taxAmount > 0 ? (
          <ChagresMainBlock sx={{ ml: 2, mr: 4 }}>
            <ChagresTitle>
              <i>Tax:</i>
            </ChagresTitle>
            <ChagresValue>
              <i>{formatPrice(charges?.taxAmount)}</i>
            </ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        {charges?.servicingFee?.ordersCoFee &&
        charges?.servicingFee?.ordersCoFee > 0 ? (
          <ChagresMainBlock sx={{ ml: 2, mr: 4 }}>
            <ChagresTitle>
              <i>OrdersCo Fee:</i>
            </ChagresTitle>
            <ChagresValue>
              <i>{formatPrice(charges?.servicingFee?.ordersCoFee)}</i>
            </ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        {charges?.servicingFee?.processingFee &&
        charges?.servicingFee?.processingFee > 0 ? (
          <ChagresMainBlock sx={{ ml: 2, mr: 4 }}>
            <ChagresTitle>
              <i>Processing Fee:</i>
            </ChagresTitle>
            <ChagresValue>
              <i>{formatPrice(charges?.servicingFee?.processingFee)}</i>
            </ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        {charges?.servicingFee?.marketplaceFee &&
        charges?.servicingFee?.marketplaceFee > 0 ? (
          <ChagresMainBlock sx={{ ml: 2, mr: 4 }}>
            <ChagresTitle>
              <i>MarketplaceFee Fee:</i>
            </ChagresTitle>
            <ChagresValue>
              <i>{formatPrice(charges?.servicingFee?.marketplaceFee)}</i>
            </ChagresValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        <hr className="line-header" />
        <ChagresMainBlock sx={{ mt: 1.5 }}>
          <ChagresBalanceTitle>Total:</ChagresBalanceTitle>
          <ChagresBalanceValue>
            {formatPrice(charges?.total)}
          </ChagresBalanceValue>
        </ChagresMainBlock>
        {charges?.rewardAmount ? (
          <ChagresMainBlock>
            <ChagresBalanceTitle>Reward Applied:</ChagresBalanceTitle>
            <ChagresBalanceValue>
              {formatPrice(charges?.rewardAmount || 0, "en-US", "USD", true)}
            </ChagresBalanceValue>
          </ChagresMainBlock>
        ) : (
          <></>
        )}
        <ChagresMainBlock>
          <ChagresBalanceTitle>
            {isRefundDueShow ? "Refund Due:" : "Balance Due:"}
          </ChagresBalanceTitle>
          <ChagresBalanceValue>
            {isRefundDueShow
              ? `(${formatPrice(charges?.balanceDue)})`
              : formatPrice(charges?.balanceDue)}
          </ChagresBalanceValue>
        </ChagresMainBlock>
      </ChargesMain>
      <hr className="line-header" />
    </>
  );
};

export default Charges;
